/**
 * Demo Edge Component
 *
 * Custom smooth step edge for the demo workflow diagram.
 * Shows dashed lines with animated particles flowing along the path
 * to demonstrate agent communication.
 *
 * @module components/home/multi-agent/edges/demo-edge
 */

import { memo, useId } from "react"
import { BaseEdge, getSmoothStepPath } from "@xyflow/react"
import type { EdgeProps } from "@xyflow/react"

/** Primary green color for particles */
const PARTICLE_COLOR = "#3B9E4F"

/** Edge stroke color */
const STROKE_COLOR = "#6b7280"

/**
 * DemoEdge renders a smooth step path with animated particles
 * flowing along the connection to show data/task flow.
 */
function DemoEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}: EdgeProps) {
  const uniqueId = useId()

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 8,
  })

  return (
    <>
      {/* Glow effect */}
      <BaseEdge
        id={`${id}-glow`}
        path={edgePath}
        style={{
          stroke: PARTICLE_COLOR,
          strokeWidth: 8,
          opacity: 0.1,
          filter: "blur(4px)",
        }}
      />

      {/* Main edge line */}
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: STROKE_COLOR,
          strokeWidth: 2,
          strokeDasharray: "6 4",
          fill: "none",
        }}
      />

      {/* Particle animation */}
      <g>
        {/* Define the motion path */}
        <defs>
          <path id={`motion-path-${uniqueId}`} d={edgePath} fill="none" />
        </defs>

        {/* Particle 1 - main */}
        <circle r="4" fill={PARTICLE_COLOR} opacity="0.9">
          <animateMotion
            dur="2.5s"
            repeatCount="indefinite"
            path={edgePath}
          />
          <animate
            attributeName="opacity"
            values="0.9;0.5;0.9"
            dur="2.5s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Particle 2 - offset timing */}
        <circle r="3" fill={PARTICLE_COLOR} opacity="0.7">
          <animateMotion
            dur="2.5s"
            repeatCount="indefinite"
            path={edgePath}
            begin="0.8s"
          />
          <animate
            attributeName="opacity"
            values="0.7;0.4;0.7"
            dur="2.5s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Particle 3 - offset timing */}
        <circle r="2" fill={PARTICLE_COLOR} opacity="0.5">
          <animateMotion
            dur="2.5s"
            repeatCount="indefinite"
            path={edgePath}
            begin="1.6s"
          />
          <animate
            attributeName="opacity"
            values="0.5;0.3;0.5"
            dur="2.5s"
            repeatCount="indefinite"
          />
        </circle>
      </g>
    </>
  )
}

export const DemoEdge = memo(DemoEdgeComponent)
